import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { ClientContactRelationshipsPanel } from "@/components/ClientContactRelationshipsPanel";
import { ContactLinkedToursPanel } from "@/components/ContactLinkedToursPanel";
import { useAuth } from "@/contexts/AuthContext";
import { contactApi, orgApi } from "@/lib/entities";
import { PageHeader, PageShell } from "@/components/PageHeader";
import { DemoChip } from "@/components/Chips";
import { RoleGate } from "@/components/RoleGate";
import { Pager } from "@/pages/customers/CustomersList";
import { ActionMenu, ArchiveConfirmModal, useArchive } from "@/components/ActionMenu";
import { ContactFormDialog } from "@/pages/contacts/ContactFormDialog";
import { AdminPortalPanel } from "@/pages/contacts/AdminPortalPanel";

// Wave 4B-6 · gates converted to canonical permissions. Backend
// `/api/contacts` routes now use `perm("contacts.view/create/edit/archive")`.
// Admin auto-inherits every non-root permission at the resolver.

export default function ContactsList() {
  const { user, can } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [orgs, setOrgs] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [clientsOpen, setClientsOpen] = useState(null); // contact whose Clients drawer is open
  const [promotingId, setPromotingId] = useState(null);

  // Phase A.2.b BLOCKER FIX #2 · direct-navigation from any linked
  // Person row: /contacts/:contactId (or ?openId=<id>) auto-opens the
  // canonical Contact Detail drawer for that specific person.
  useEffect(() => {
    const search = new URLSearchParams(location.search);
    const targetId = params.contactId || search.get("openId");
    if (!targetId) return;
    (async () => {
      try {
        const c = await contactApi.get(targetId);
        setClientsOpen(c);
      } catch {
        toast.error("Person not found");
      }
    })();
  }, [params.contactId, location.search]);
  const canCreate = can("contacts.create");
  const canEdit = can("contacts.edit");
  const canArchive = can("contacts.archive");
  const canPromote = can("contacts.promote");
  const canWrite = canCreate || canEdit || canArchive;

  const promote = async (contact) => {
    if (contact.promoted_to_customer_id) {
      navigate(`/customers/${contact.promoted_to_customer_id}`);
      return;
    }
    setPromotingId(contact.id);
    try {
      const r = await contactApi.promoteToCustomer(contact.id);
      if (r.already_promoted) {
        toast.info("Customer already exists for this contact");
      } else {
        toast.success(`Customer created: ${r.customer.display_name}`);
      }
      navigate(`/customers/${r.customer.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Promotion failed");
    } finally {
      setPromotingId(null);
    }
  };

  const load = async () => {
    try {
      // W4 remediation · use canonical `/contacts/search` when the
      // operator is actively searching (exact-name / prefix / partial
      // / email / phone ranking lives there). Fall back to the plain
      // list endpoint for the un-searched default view — that path
      // owns the named-first alphabetical ordering.
      const d = q
        ? await contactApi.search({ q, page, page_size: 25 })
        : await contactApi.list({ page, page_size: 25 });
      setRows(d.results);
      setTotal(d.total);
    } catch (e) {
      toast.error("Failed to load contacts");
    }
  };

  const archive = useArchive({
    entityLabel: "Contact",
    apiArchive: contactApi.archive,
    onSuccess: async (row) => {
      toast.success(`Archived ${row.preferred_name || row.first_name} ${row.last_name}`);
      await load();
    },
  });

  useEffect(() => {
    load(); /* eslint-disable-next-line */
  }, [q, page]);
  useEffect(() => {
    orgApi.list({ page_size: 200 }).then((d) => setOrgs(d.results)).catch(() => {});
  }, []);

  return (
    <RoleGate permission="contacts.view">
      <PageShell>
        <PageHeader
          testid="page-contacts"
          title="People"
          subtitle={`${total.toLocaleString()} total · each person can hold multiple roles across multiple organizations`}
          right={
            canCreate && (
              <button
                data-testid="new-contact-btn"
                onClick={() => {
                  setEditing(null);
                  setOpen(true);
                }}
                className="inline-flex items-center gap-1.5 bg-coach-blue hover:bg-deep-road-navy text-white text-[12.5px] font-medium px-3 py-1.5 rounded-sm"
              >
                <Plus size={14} /> New Contact
              </button>
            )
          }
        />

        <div className="bg-white border border-coach-silver rounded-md">
          <div className="px-4 py-3 border-b border-coach-silver">
            <input
              data-testid="contacts-search"
              value={q}
              onChange={(e) => {
                setPage(1);
                setQ(e.target.value);
              }}
              placeholder="Search by name or email…"
              className="w-full max-w-md border border-coach-silver rounded-sm px-3 py-1.5 text-[13px]"
            />
          </div>
          <table className="w-full text-[13px]" data-testid="contacts-table">
            <thead className="bg-road-white text-[10.5px] uppercase tracking-widest text-steel">
              <tr>
                <th className="text-left px-4 py-2">Name</th>
                <th className="text-left px-4 py-2">Title</th>
                <th className="text-left px-4 py-2">Email</th>
                <th className="text-left px-4 py-2">Mobile</th>
                <th className="w-24" />
              </tr>
            </thead>
            <tbody>
              {rows && rows.map((r) => {
                const display = `${r.preferred_name || r.first_name || ""} ${r.last_name || ""}`.trim();
                const hasName = !!(r.last_name || (r.first_name && !r.first_name.includes("@")) || (r.preferred_name && !r.preferred_name.includes("@")));
                const label = display || r.primary_email || "Unnamed contact";
                return (
                <tr key={r.id} data-testid={`contact-row-${r.id}`} className="border-t border-coach-silver/60 hover:bg-road-white">
                  <td className="px-4 py-2">
                    {/* Owner blocker fix (2026-02) · Person name is the
                        primary interactive target. Uses canonical
                        `/contacts/:contactId` route so duplicate names
                        remain safe — identity is the row id. */}
                    <Link
                      to={`/contacts/${r.id}`}
                      data-testid={`contact-open-${r.id}`}
                      className="text-coach-blue hover:underline focus-visible:outline-2 focus-visible:outline-coach-blue rounded-sm font-medium"
                    >
                      {hasName ? label : <span className="italic text-steel">{label}</span>}
                    </Link>{" "}
                    {r.is_demo && <DemoChip />}
                  </td>
                  <td className="px-4 py-2 text-steel">{r.title || "—"}</td>
                  <td className="px-4 py-2 text-steel">{r.primary_email || "—"}</td>
                  <td className="px-4 py-2 text-steel">{r.mobile || "—"}</td>
                  <td className="px-4 py-2 text-right">
                    <Link
                      to={`/contacts/${r.id}`}
                      data-testid={`contact-view-${r.id}`}
                      className="text-[11.5px] text-coach-blue hover:underline mr-3 focus-visible:outline-2 focus-visible:outline-coach-blue rounded-sm"
                      title="Open Person Detail"
                    >
                      View
                    </Link>
                    {canPromote && (
                      <button
                        data-testid={`contact-promote-${r.id}`}
                        onClick={() => promote(r)}
                        disabled={promotingId === r.id}
                        className="text-[11.5px] text-coach-blue hover:underline mr-3 disabled:opacity-60"
                        title={
                          r.promoted_to_customer_id
                            ? "View promoted customer"
                            : "Promote this contact to a Customer"
                        }
                      >
                        {r.promoted_to_customer_id
                          ? "View Customer"
                          : promotingId === r.id
                            ? "Promoting…"
                            : "Promote"}
                      </button>
                    )}
                    <ActionMenu
                      testid={`contact-actions-${r.id}`}
                      onEdit={canEdit ? () => { setEditing(r); setOpen(true); } : undefined}
                      onArchive={canArchive && !r.is_archived ? () => archive.request({ ...r, display_name: `${r.preferred_name || r.first_name} ${r.last_name}` }) : undefined}
                    />
                  </td>
                </tr>
              );})}
              {rows === null && (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-steel text-[13px]">
                    Loading contacts…
                  </td>
                </tr>
              )}
              {rows && rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-steel">
                    No contacts found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <Pager page={page} setPage={setPage} total={total} pageSize={25} />
        </div>

        {open && (
          <ContactFormDialog
            open={open}
            editing={editing}
            orgs={orgs}
            onClose={() => setOpen(false)}
            onSaved={() => {
              setOpen(false);
              load();
            }}
          />
        )}
        {clientsOpen && (
          <ContactDrawer
            contact={clientsOpen}
            onClose={() => {
              setClientsOpen(null);
              // If we arrived via /contacts/:id deep link, drop back to
              // the plain People list so Back/refresh don't reopen the
              // drawer unexpectedly.
              if (params.contactId) navigate("/contacts");
            }}
          />
        )}
        <ArchiveConfirmModal {...archive.modalProps} />
      </PageShell>
    </RoleGate>
  );
}

// Phase A.2.b · Contact Detail drawer with Clients + Tours tabs.
function ContactDrawer({ contact, onClose }) {
  const [tab, setTab] = useState("clients");
  return (
    <div
      className="fixed inset-0 z-40 bg-black/40 flex justify-end"
      data-testid={`contact-clients-drawer-${contact.id}`}
      onClick={onClose}
      onKeyDown={(e) => e.key === "Escape" && onClose()}
    >
      <div
        className="bg-white w-[560px] max-w-full h-full overflow-y-auto shadow-xl border-l border-coach-silver flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-carbon text-white px-4 py-3 flex items-center justify-between shrink-0">
          <div>
            <div className="text-[10.5px] uppercase tracking-widest text-coach-silver">
              Contact
            </div>
            <div className="text-[15px] font-medium mt-0.5">
              {contact.preferred_name || contact.first_name} {contact.last_name}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="contact-clients-drawer-close"
            className="text-coach-silver hover:text-white text-[12px] focus-visible:outline-2 focus-visible:outline-touring-lime px-2 py-1 rounded-sm"
          >
            Close
          </button>
        </div>
        <div className="border-b border-coach-silver flex bg-road-white">
          {[
            ["clients", "Clients"],
            ["tours", "Tours"],
            ["portal", "Portal"],
          ].map(([k, l]) => (
            <button
              key={k}
              type="button"
              onClick={() => setTab(k)}
              data-testid={`contact-drawer-tab-${k}`}
              className={`px-4 py-2 text-[12.5px] border-b-2 -mb-px focus-visible:outline-2 focus-visible:outline-coach-blue rounded-sm ${
                tab === k
                  ? "border-coach-blue text-coach-blue font-medium"
                  : "border-transparent text-steel hover:text-carbon"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
        <div className="p-4 flex-1 min-h-0 overflow-y-auto">
          {tab === "clients" && (
            <ClientContactRelationshipsPanel mode="contact" contactId={contact.id} />
          )}
          {tab === "tours" && (
            <ContactLinkedToursPanel contactId={contact.id} />
          )}
          {tab === "portal" && (
            <AdminPortalPanel contactId={contact.id} contactEmail={contact.primary_email} />
          )}
        </div>
      </div>
    </div>
  );
}
